import { type ReactElement } from 'react';
import { layoutSizes } from '@/theme/layoutSizes';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { Box, PageShell } from '@/ui/primitives';
import { ExecutionTablesCarouselSection } from './landing/ExecutionTablesCarouselSection';
import { FlowStepsSection } from './landing/FlowStepsSection';
import { landingSizes } from './landing/landingData';
import { LandingHeroSection } from './landing/LandingHeroSection';
import { QualityHighlightsCarouselSection } from './landing/QualityHighlightsCarouselSection';
import { TestingChangedSection } from './landing/TestingChangedSection';
import { useLandingViewModel } from './landing/useLandingViewModel';

export const LandingContent = (): ReactElement => {
  const viewModel = useLandingViewModel();
  const { headerWidth, panelsWidth, testingChangedWidth, carouselSectionWidth } = landingSizes;
  const chartHeight = 200;
  const staggerStepMs = 400;
  const staggerBaseMs = 220;
  const toggleButtonHeight = layoutSizes.sidebar.itemMinHeight;
  return (
    <Box
      className="landing-page"
      sx={(theme) => ({
        position: 'relative',
        isolation: 'isolate',
        color: 'text.primary',
        backgroundColor: 'background.default',
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 0%, ${theme.app.colors.surface} 24%, ${theme.app.colors.bg} 62%, ${theme.app.colors.bg} 100%), radial-gradient(circle at calc(50% + min(340px, 20vw)) 20%, ${theme.app.colors.primary}2f 0%, ${theme.app.colors.primary}14 28%, rgba(0,0,0,0) 52%), linear-gradient(120deg, ${theme.app.colors.surfaceAlt} 0%, ${theme.app.colors.surface} 42%, ${theme.app.colors.primary}28 62%, ${theme.app.colors.bg} 84%, ${theme.app.colors.bg} 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          right: {
            xs: '-12px',
            sm: '-12px',
            md: s('none'),
            xl: `calc((100vw - ${theme.breakpoints.values.xl}px) / 2 + ${s('3xl')})`,
          },
          top: { xs: 0, sm: -12, md: -24 },
          width: { xs: 260, sm: 340, md: 420, xl: 460 },
          aspectRatio: '1 / 1',
          backgroundImage: 'url(/logo.svg)',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'contain',
          opacity: 0.14,
          transform: 'rotate(-18deg)',
          filter: 'blur(0.5px)',
          pointerEvents: 'none',
          zIndex: 0,
          willChange: 'transform',
          animation: `landingPageLogoFloat calc(${theme.app.motion.durations.slow} * 24) ${theme.app.motion.easing.standard} infinite`,
        },
        '@keyframes landingPageLogoFloat': {
          '0%': { transform: 'translateY(0) rotate(-18deg)' },
          '50%': { transform: 'translateY(-6px) rotate(-18deg)' },
          '100%': { transform: 'translateY(0) rotate(-18deg)' },
        },
      })}
    >
      <PageShell maxWidth="xl" paddingX="lg" paddingY="xl" paddingTop="none" paddingBottom="none">
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Stack
            gap="3xl"
            sx={(theme) => ({
              '--landing-stagger-step': `${staggerStepMs}ms`,
              '--landing-stagger-base': `${staggerBaseMs}ms`,
              '& > *': {
                opacity: viewModel.staggerReady ? 1 : 0,
                transform: viewModel.staggerReady ? 'translateY(0)' : `translateY(${s('lg')})`,
                transitionProperty: 'opacity, transform',
                transitionDuration: '3000ms',
                transitionTimingFunction: theme.app.motion.easing.standard,
              },
              '& > *:nth-of-type(1)': { transitionDelay: 'var(--landing-stagger-base)' },
              '& > *:nth-of-type(2)': {
                transitionDelay: 'calc(var(--landing-stagger-base) + var(--landing-stagger-step))',
              },
              '& > *:nth-of-type(3)': {
                transitionDelay:
                  'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 2))',
              },
              '& > *:nth-of-type(4)': {
                transitionDelay:
                  'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 3))',
              },
              '& > *:nth-of-type(5)': {
                transitionDelay:
                  'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 4))',
              },
              '& > *:nth-of-type(6)': {
                transitionDelay:
                  'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 5))',
              },
              '& > *:nth-of-type(7)': {
                transitionDelay:
                  'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 6))',
              },
              '& > *:nth-of-type(8)': {
                transitionDelay:
                  'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 7))',
              },
              '& > *:nth-of-type(9)': {
                transitionDelay:
                  'calc(var(--landing-stagger-base) + (var(--landing-stagger-step) * 8))',
              },
            })}
          >
            <Box sx={{ px: s('md'), pb: s('3xl') }}>
              <Stack gap="none" sx={{ gap: `calc(${s('2xl')} + ${s('2xl')})` }}>
                <LandingHeroSection headerWidth={headerWidth} />
                <TestingChangedSection width={testingChangedWidth} />
                <FlowStepsSection width={panelsWidth} />
                <QualityHighlightsCarouselSection
                  width={carouselSectionWidth}
                  chartHeight={chartHeight}
                  toggleButtonHeight={toggleButtonHeight}
                  rangeTab={viewModel.chart.rangeTab}
                  onRangeTabChange={viewModel.chart.setRangeTab}
                  statusCards={viewModel.chart.statusCards}
                  metricCardsForRange={viewModel.chart.metricCardsForRange}
                  chartTab={viewModel.chart.chartTab}
                  onChartTabChange={viewModel.chart.setChartTab}
                  statusTrendChartData={viewModel.chart.statusTrendChartData}
                  passRateChartData={viewModel.chart.passRateChartData}
                  frequencyChartData={viewModel.chart.frequencyChartData}
                  durationChartData={viewModel.chart.durationChartData}
                  carouselIndex={viewModel.carousels.carouselIndex}
                  direction={viewModel.carousels.carouselDirection}
                  onPrev={viewModel.carousels.onPrevSlide}
                  onNext={viewModel.carousels.onNextSlide}
                  onSelect={viewModel.carousels.setCarouselIndex}
                  slides={viewModel.carousels.carouselSlides}
                />
                <ExecutionTablesCarouselSection
                  width={carouselSectionWidth}
                  carouselIndex={viewModel.carousels.tablesCarouselIndex}
                  direction={viewModel.carousels.tablesCarouselDirection}
                  onPrev={viewModel.carousels.onPrevTableSlide}
                  onNext={viewModel.carousels.onNextTableSlide}
                  onSelect={viewModel.carousels.setTablesCarouselIndex}
                  slides={viewModel.carousels.tablesCarouselSlides}
                  suiteExecutionsQuery={viewModel.tableQueries.suiteExecutionsQuery}
                  suitesQuery={viewModel.tableQueries.suitesQuery}
                />
              </Stack>
            </Box>
          </Stack>
        </Box>
      </PageShell>
    </Box>
  );
};
